<p-toast></p-toast>
<p-confirmDialog header="Confirmation" icon="pi pi-exclamation-triangle"></p-confirmDialog>
@if (formLoading) {
  <div class="progress-spinner">
    <app-spinner [formLoading]="formLoading"></app-spinner>
  </div>
}

<!-- ===================================================================== -->
<!-- DASHBOARD HEADER                                                        -->
<!-- ===================================================================== -->
<div class="dashboard-header">
  <h2 style="margin:0 0 4px 0; font-size:1.3rem;">Quality Test Dashboard</h2>
  @if (dashboard) {
    <span class="dashboard-subtitle">
      {{ dashboard.totalProperties | number }} properties &mdash; as of {{ dashboard.asOfDate | date:'MM-dd-yyyy HH:mm' }}
    </span>
  }
</div>

<!-- ===================================================================== -->
<!-- KPI PILLS — click to filter results table                              -->
<!-- ===================================================================== -->
@if (dashboard) {
  <div class="kpi-row">
    <div class="kpi-card" title="Show all statuses" (click)="filterByStatus(null)">
      <div class="kpi-label">Properties</div>
      <div class="kpi-value">{{ dashboard.totalProperties | number }}</div>
    </div>
    <div class="kpi-card kpi-fail" [class.kpi-active]="activeStatusFilter === 'FAIL'"
         title="Click to filter by FAIL" (click)="filterByStatus('FAIL')">
      <div class="kpi-label">Total FAILs</div>
      <div class="kpi-value">{{ getTotalFails(dashboard.byCategory) | number }}</div>
      <div class="kpi-hint">click to filter</div>
    </div>
    <div class="kpi-card kpi-warn" [class.kpi-active]="activeStatusFilter === 'WARN'"
         title="Click to filter by WARN" (click)="filterByStatus('WARN')">
      <div class="kpi-label">Total WARNs</div>
      <div class="kpi-value">{{ getTotalWarns(dashboard.byCategory) | number }}</div>
      <div class="kpi-hint">click to filter</div>
    </div>
    <div class="kpi-card kpi-pass" [class.kpi-active]="activeStatusFilter === 'PASS'"
         title="Click to filter by PASS" (click)="filterByStatus('PASS')">
      <div class="kpi-label">Total PASSes</div>
      <div class="kpi-value">{{ getTotalPasses(dashboard.byCategory) | number }}</div>
      <div class="kpi-hint">click to filter</div>
    </div>
  </div>

  <!-- By-Category summary table — click a row to filter results by that category -->
  <p-table [value]="dashboard.byCategory" styleClass="p-datatable-striped p-datatable-sm"
           [autoLayout]="true" [responsive]="true" style="margin-bottom:1.5rem;">
    <ng-template pTemplate="header">
      <tr>
        <th>Category <small style="font-weight:400; color:#888">(click to filter)</small></th>
        <th style="text-align:center">PASS</th>
        <th style="text-align:center">WARN</th>
        <th style="text-align:center">FAIL</th>
        <th style="text-align:center">INFO</th>
        <th style="text-align:center">Health Score</th>
        <th style="text-align:center; width:70px">Refresh</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-row>
      <tr class="category-row" [class.category-active]="activeCategoryFilter === row.testCategory"
          (click)="filterByCategory(row.testCategory)" title="Filter results by {{ row.testCategory }}">
        <td><strong>{{ row.testCategory }}</strong></td>
        <td style="text-align:center; color:#155724; font-weight:600">{{ row.passCount | number }}</td>
        <td style="text-align:center; color:#856404; font-weight:600">{{ row.warnCount | number }}</td>
        <td [style.color]="row.failCount > 0 ? '#721c24' : '#155724'" style="text-align:center; font-weight:600">{{ row.failCount | number }}</td>
        <td style="text-align:center; color:#555">{{ row.infoCount | number }}</td>
        <td style="text-align:center;">
          @if (row.healthScore !== null) {
            <span [class]="'health-badge ' + getCategoryHealthClass(row.healthScore)">
              {{ row.healthScore }}%
            </span>
          } @else {
            <span style="color:#aaa">—</span>
          }
        </td>
        <!-- Refresh button — reruns all tests for this category's parent type -->
        <td style="text-align:center;" (click)="$event.stopPropagation()">
          <button pButton pRipple
                  class="p-button-text p-button-sm p-button-rounded"
                  [icon]="refreshingCategory === getCategoryParent(row.testCategory) ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"
                  [disabled]="!!refreshingCategory"
                  (click)="refreshCategoryParent(row.testCategory, $event)"
                  [title]="'Re-run all ' + getRefreshParentLabel(row.testCategory) + ' tests'">
          </button>
        </td>
      </tr>
    </ng-template>
  </p-table>
} @else if (dashboardLoading) {
  <div style="padding:1rem; color:#666">Loading dashboard summary...</div>
}

<!-- ===================================================================== -->
<!-- ACTIVE FILTER BANNER                                                    -->
<!-- ===================================================================== -->
@if (getActiveFilterLabels().length > 0) {
  <div class="filter-banner">
    <i class="pi pi-filter"></i>
    <span>Active filters:</span>
    @for (label of getActiveFilterLabels(); track label) {
      <span class="filter-chip">{{ label }}</span>
    }
    <button class="filter-clear" (click)="reloadPage()">Clear All</button>
  </div>
}

<!-- ===================================================================== -->
<!-- CONTROLS / ACTION BUTTONS                                               -->
<!-- ===================================================================== -->
<div class="controls-row" *ngIf="formReady" [formGroup]="form1">
  <p-multiSelect [options]="properties" formControlName="shortTermRentals" appendTo="body"
    defaultLabel="Select Property (optional)" optionLabel="propertyCode" display="chip"
    style="min-width:260px;">
  </p-multiSelect>

  <div class="btn-group">
    <button pButton pRipple class="p-button-outlined p-button-sm"
      (click)="reloadPage()">Clear Filters</button>
    <button pButton pRipple class="p-button p-button-sm p-button-warning"
      (click)="testAllListingQualityStatus()">Run All Properties</button>
    <button pButton pRipple class="p-button-outlined p-button-sm"
      (click)="loadDashboard()">Refresh Summary</button>
  </div>
</div>

<!-- ===================================================================== -->
<!-- SHARED FIX OVERLAY PANEL                                               -->
<!-- Shared by all rows — selectedRow is set on click before toggle         -->
<!-- ===================================================================== -->
<p-overlayPanel #fixPanel [showCloseIcon]="true" [style]="{width:'420px', 'max-width':'92vw'}">
  @if (selectedRow) {
    <div class="fix-panel">
      <div class="fix-panel-status"
           [ngStyle]="{'background-color': getResStatusBg(selectedRow.testResultStatus),
                       'color': getResStatusColor(selectedRow.testResultStatus)}">
        <strong>{{ selectedRow.testResultStatus }}</strong>
        <span style="margin:0 6px; opacity:0.5;">|</span>
        <span>{{ selectedRow.testName }}</span>
      </div>

      @if (selectedRow.testResultValue) {
        <div class="fix-panel-field">
          <span class="fix-panel-label">Actual:</span>
          <span>{{ selectedRow.testResultValue }}</span>
        </div>
      }
      @if (selectedRow.testExpectedValue) {
        <div class="fix-panel-field">
          <span class="fix-panel-label">Expected:</span>
          <span style="color:#155724; font-weight:600;">{{ selectedRow.testExpectedValue }}</span>
        </div>
      }

      <!-- Resolution steps — the main reason the Fix panel exists -->
      @if (selectedRow.resolutionSteps) {
        <div class="resolution-box" style="margin-top:10px;">
          <pre class="detail-pre resolution-pre">{{ selectedRow.resolutionSteps }}</pre>
        </div>
      } @else {
        <div style="margin-top:10px; padding:10px; background:#fff8e1; border-radius:4px; border-left:3px solid #ffc107; font-size:0.87rem; color:#555;">
          <i class="pi pi-info-circle" style="margin-right:6px; color:#ffc107;"></i>
          No resolution steps yet. Click <strong>Run Ledger Integrity</strong> (or the relevant test) to reload fresh results — resolution steps will appear after re-running.
        </div>
      }

      <!-- Action buttons -->
      <div class="fix-panel-actions">
        @if (selectedRow.shortTermRentalId) {
          <button pButton pRipple class="p-button-outlined p-button-sm" icon="pi pi-home"
                  label="Open Property"
                  (click)="openProperty(selectedRow.shortTermRentalId)"></button>
        }
        @if (hasSourceLink(selectedRow)) {
          <button pButton pRipple class="p-button-outlined p-button-sm p-button-info" icon="pi pi-external-link"
                  [label]="'Open ' + selectedRow.sourceType"
                  (click)="openSource(selectedRow)"></button>
        }
        <button pButton pRipple class="p-button-sm p-button-text" icon="pi pi-list"
                label="Full Detail"
                (click)="openDetail(selectedRow); fixPanel.hide()"></button>
      </div>
    </div>
  }
</p-overlayPanel>

<!-- ===================================================================== -->
<!-- RAW RESULTS TABLE                                                       -->
<!-- Click any row to open full detail dialog                               -->
<!-- ===================================================================== -->
<p-table #pt [value]="listingQualityStatuss" [lazy]="true" (onLazyLoad)="loadLazy($event)"
  dataKey="qualityTestId"
  [loading]="loading"
  [paginator]="true" [rows]="100" [rowsPerPageOptions]="[100,500,1000,2000]"
  showCurrentPageReport="true"
  currentPageReportTemplate="{totalRecords} results. Page {currentPage} of {totalPages}"
  [totalRecords]="recordTotal"
  sortMode="multiple" [multiSortMeta]="multiSortMeta"
  styleClass="p-datatable-striped p-datatable-sm"
  [resizableColumns]="true" [autoLayout]="true" [responsive]="true">

  <ng-template pTemplate="header">
    <!-- Sortable column headers -->
    <tr>
      <th pSortableColumn="testResultStatus" style="width:80px">
        Status <p-sortIcon field="testResultStatus"></p-sortIcon>
      </th>
      <th pSortableColumn="shortTermRentalId" style="width:70px">
        STR Id <p-sortIcon field="shortTermRentalId"></p-sortIcon>
      </th>
      <th pSortableColumn="testSubjectName" style="width:140px">
        Property <p-sortIcon field="testSubjectName"></p-sortIcon>
      </th>
      <th pSortableColumn="testCategory" style="width:130px">
        Category <p-sortIcon field="testCategory"></p-sortIcon>
      </th>
      <th pSortableColumn="testName" style="width:160px">
        Test Name <p-sortIcon field="testName"></p-sortIcon>
      </th>
      <th pSortableColumn="testResultValue" style="width:140px">
        Result <p-sortIcon field="testResultValue"></p-sortIcon>
      </th>
      <th pSortableColumn="testExpectedValue" style="width:150px">
        Expected <p-sortIcon field="testExpectedValue"></p-sortIcon>
      </th>
      <th style="width:160px">Reservation / Source</th>
      <th>Description</th>
      <th style="width:110px; text-align:center;">Actions</th>
    </tr>
    <!-- Column filter row -->
    <tr>
      <th>
        <p-dropdown [style]="{'width':'100%'}" [options]="testResultStatusValues" appendTo="body"
          [(ngModel)]="defaultTestResultStatus"
          (onChange)="activeStatusFilter=$event.value; pt.filter($event.value, 'testResultStatus', 'in')"
          styleClass="p-column-filter" placeholder="All" [showClear]="true"
          optionLabel="name" optionValue="value">
        </p-dropdown>
      </th>
      <th>
        <input pInputText type="text" style="width:100%"
          (input)="pt.filter($event.target['value'], 'shortTermRentalId', 'in')" placeholder="Filter">
      </th>
      <th>
        <input pInputText type="text" style="width:100%"
          (input)="pt.filter($event.target['value'], 'testSubjectName', 'contains')" placeholder="Filter">
      </th>
      <th>
        <p-dropdown [style]="{'width':'100%'}" [options]="testCategoryValues" appendTo="body"
          [(ngModel)]="filterTestCategory"
          (onChange)="activeCategoryFilter=$event.value; pt.filter($event.value, 'testCategory', 'in')"
          styleClass="p-column-filter" placeholder="All" [showClear]="true"
          optionLabel="name" optionValue="value">
        </p-dropdown>
      </th>
      <th>
        <p-dropdown [style]="{'width':'100%'}" [options]="testNameValues" appendTo="body"
          [(ngModel)]="filterTestName"
          (onChange)="pt.filter($event.value, 'testName', 'in')"
          styleClass="p-column-filter" placeholder="All" [showClear]="true"
          optionLabel="name" optionValue="value">
        </p-dropdown>
      </th>
      <th>
        <input pInputText type="text" style="width:100%"
          (input)="pt.filter($event.target['value'], 'testResultValue', 'contains')" placeholder="Filter">
      </th>
      <th>
        <input pInputText type="text" style="width:100%"
          (input)="pt.filter($event.target['value'], 'testExpectedValue', 'contains')" placeholder="Filter">
      </th>
      <th></th>
      <th>
        <input pInputText type="text" style="width:100%"
          (input)="pt.filter($event.target['value'], 'testResultDesc', 'contains')" placeholder="Filter">
      </th>
      <th></th>
    </tr>
  </ng-template>

  <ng-template pTemplate="body" let-row>
    <!-- Entire row is clickable — opens full detail dialog -->
    <tr class="result-row" (click)="openDetail(row)" title="Click to view full detail &amp; resolution steps">
      <!-- Status -->
      <td [ngStyle]="{'background-color': getResStatusBg(row.testResultStatus), 'color': getResStatusColor(row.testResultStatus)}"
          style="font-weight:700; white-space:nowrap; text-align:center;">
        {{ row.testResultStatus }}
      </td>
      <!-- STR Id — links to property -->
      <td>
        @if (row.shortTermRentalId) {
          <a class="link-cell" (click)="$event.stopPropagation(); openProperty(row.shortTermRentalId)"
             title="Open property {{ row.shortTermRentalId }}">
            {{ row.shortTermRentalId }}
          </a>
        } @else {
          <span style="color:#aaa">—</span>
        }
      </td>
      <!-- Property name — links to property -->
      <td>
        @if (row.shortTermRentalId) {
          <a class="link-cell" (click)="$event.stopPropagation(); openProperty(row.shortTermRentalId)"
             title="Open property">
            {{ row.testSubjectName }}
          </a>
        } @else {
          {{ row.testSubjectName }}
        }
      </td>
      <!-- Category — click to filter -->
      <td>
        @if (row.testCategory) {
          <a class="link-cell link-filter" (click)="$event.stopPropagation(); filterByCategory(row.testCategory)"
             title="Filter by {{ row.testCategory }}">
            {{ row.testCategory }}
          </a>
        }
      </td>
      <!-- Test Name -->
      <td>{{ row.testName }}</td>
      <!-- Result Value -->
      <td style="word-break:break-word;">{{ row.testResultValue }}</td>
      <!-- Expected Value -->
      <td style="word-break:break-word; color:#555;">{{ row.testExpectedValue }}</td>
      <!-- Reservation / Source — clickable link -->
      <td>
        @if (hasSourceLink(row)) {
          <a class="link-cell source-link" (click)="$event.stopPropagation(); openSource(row)"
             title="Open {{ row.sourceType }} #{{ row.sourceMask || row.sourceId }}">
            <i class="pi" [ngClass]="{
              'pi-calendar-times': row.sourceType === 'RESERVATION',
              'pi-home':           row.sourceType === 'PROPERTY',
              'pi-cog':            row.sourceType === 'JOB'
            }"></i>
            <span>{{ getSourceLabel(row) }}</span>
          </a>
        } @else {
          <span style="color:#aaa; font-size:0.82rem;">re-run to load</span>
        }
      </td>
      <!-- Description -->
      <td style="max-width:400px; word-break:break-word;">
        <pre style="margin:0; font-family:inherit; font-size:inherit; white-space:pre-wrap;">{{ row.testResultDesc }}</pre>
      </td>
      <!-- Actions: Fix | Detail | Delete -->
      <td style="white-space:nowrap; text-align:center;">
        <button pButton pRipple class="p-button-success p-button-sm fix-btn"
                icon="pi pi-wrench" label="Fix"
                (click)="$event.stopPropagation(); selectedRow=row; fixPanel.toggle($event)"
                title="Show resolution steps"></button>
        <i class="pi pi-search" style="color:#3b82f6; cursor:pointer; margin-left:8px; font-size:110%;"
           (click)="$event.stopPropagation(); openDetail(row)" title="Full detail"></i>
        <i class="pi pi-trash" style="color:#cc0000; cursor:pointer; margin-left:8px; font-size:110%;"
           (click)="$event.stopPropagation(); onRowDelete(row)" title="Delete"></i>
      </td>
    </tr>
  </ng-template>

  <ng-template pTemplate="emptymessage">
    <tr>
      <td colspan="10" style="text-align:center; padding:2rem; color:#666;">
        No results found. Run a quality test to populate results.
      </td>
    </tr>
  </ng-template>
</p-table>

<!-- ===================================================================== -->
<!-- FULL DETAIL DIALOG                                                      -->
<!-- ===================================================================== -->
<p-dialog [(visible)]="showDetail"
          [header]="selectedRow ? 'Test Result #' + selectedRow.qualityTestId : 'Detail'"
          [modal]="true" [style]="{width:'720px', 'max-width':'95vw'}"
          [closable]="true" [draggable]="true" [resizable]="false"
          (onHide)="closeDetail()">

  @if (selectedRow) {
    <div class="detail-content">

      <!-- Status bar -->
      <div class="detail-status-bar"
           [ngStyle]="{'background-color': getResStatusBg(selectedRow.testResultStatus),
                       'color': getResStatusColor(selectedRow.testResultStatus)}">
        <strong style="font-size:1rem;">{{ selectedRow.testResultStatus }}</strong>
        <span style="margin:0 8px; opacity:0.5;">|</span>
        <span>{{ selectedRow.testCategory }}</span>
        <span style="margin:0 8px; opacity:0.5;">/</span>
        <span>{{ selectedRow.testName }}</span>
      </div>

      <div class="detail-grid">

        <!-- Subject / Property -->
        <div class="detail-section">
          <h4 class="detail-section-title">Subject</h4>
          <div class="detail-row">
            <span class="detail-label">Property</span>
            @if (selectedRow.shortTermRentalId) {
              <a class="link-cell" (click)="openProperty(selectedRow.shortTermRentalId)">
                {{ selectedRow.testSubjectName }} (STR #{{ selectedRow.shortTermRentalId }})
              </a>
            } @else {
              <span>{{ selectedRow.testSubjectName || '—' }}</span>
            }
          </div>
          @if (selectedRow.sourceType) {
            <div class="detail-row">
              <span class="detail-label">{{ selectedRow.sourceType === 'RESERVATION' ? 'Reservation' : selectedRow.sourceType }}</span>
              @if (hasSourceLink(selectedRow)) {
                <a class="link-cell" (click)="openSource(selectedRow)">
                  #{{ selectedRow.sourceMask || selectedRow.sourceId }}
                  <i class="pi pi-external-link" style="font-size:0.75rem; margin-left:4px;"></i>
                </a>
              } @else {
                <span>{{ selectedRow.sourceType }}</span>
              }
            </div>
          }
        </div>

        <!-- Result values -->
        <div class="detail-section">
          <h4 class="detail-section-title">Result</h4>
          <div class="detail-row">
            <span class="detail-label">Actual</span>
            <span>{{ selectedRow.testResultValue || '—' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Expected</span>
            <span style="color:#155724; font-weight:600;">{{ selectedRow.testExpectedValue || '—' }}</span>
          </div>
        </div>

        <!-- Description -->
        <div class="detail-section detail-full">
          <h4 class="detail-section-title">Description</h4>
          <pre class="detail-pre">{{ selectedRow.testResultDesc }}</pre>
        </div>

        <!-- Expectation from definition -->
        @if (selectedRow.testExpectationDesc) {
          <div class="detail-section detail-full">
            <h4 class="detail-section-title">What Was Expected</h4>
            <p class="detail-text">{{ selectedRow.testExpectationDesc }}</p>
          </div>
        }

        <!-- Resolution steps — most actionable section -->
        @if (selectedRow.resolutionSteps) {
          <div class="detail-section detail-full">
            <h4 class="detail-section-title">
              <i class="pi pi-directions" style="margin-right:6px;"></i>
              How to Fix
            </h4>
            <div class="resolution-box">
              <pre class="detail-pre resolution-pre">{{ selectedRow.resolutionSteps }}</pre>
            </div>
          </div>
        } @else {
          <div class="detail-section detail-full">
            <h4 class="detail-section-title">
              <i class="pi pi-directions" style="margin-right:6px; opacity:0.4;"></i>
              How to Fix
            </h4>
            <div style="padding:10px; background:#fff8e1; border-radius:4px; border-left:3px solid #ffc107; font-size:0.87rem; color:#555;">
              <i class="pi pi-info-circle" style="margin-right:6px; color:#ffc107;"></i>
              Resolution steps not yet loaded for this row. Re-run the test to populate fresh results.
            </div>
          </div>
        }

        <!-- Meta -->
        <div class="detail-section">
          <h4 class="detail-section-title">Meta</h4>
          <div class="detail-row">
            <span class="detail-label">Test ID</span>
            <span>{{ selectedRow.qualityTestId }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Category</span>
            <a class="link-cell" (click)="filterByCategory(selectedRow.testCategory); closeDetail()">{{ selectedRow.testCategory }}</a>
          </div>
          <div class="detail-row">
            <span class="detail-label">Created</span>
            <span>{{ selectedRow.createDate | date:'MM-dd-yyyy HH:mm' }}</span>
          </div>
        </div>

      </div>
    </div>

    <ng-template pTemplate="footer">
      @if (selectedRow.shortTermRentalId) {
        <button pButton pRipple class="p-button-outlined p-button-sm" icon="pi pi-home"
                label="Open Property"
                (click)="openProperty(selectedRow.shortTermRentalId)"></button>
      }
      @if (hasSourceLink(selectedRow)) {
        <button pButton pRipple class="p-button-outlined p-button-sm p-button-info" icon="pi pi-external-link"
                [label]="'Open ' + selectedRow.sourceType"
                (click)="openSource(selectedRow)"></button>
      }
      <button pButton pRipple class="p-button-outlined p-button-sm p-button-danger" icon="pi pi-trash"
              label="Delete"
              (click)="onRowDelete(selectedRow)"></button>
      <button pButton pRipple class="p-button-sm" icon="pi pi-times"
              label="Close"
              (click)="closeDetail()"></button>
    </ng-template>
  }
</p-dialog>
